import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { cleanWhitespace } from "../lib/whitespace";

const SAMPLE = "  Hello    World  \n\n   Welcome  to   Toolverse  ";

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      {label}
    </label>
  );
}

export function WhitespaceCleanerTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [trimLines, setTrimLines] = useState(true);
  const [collapseSpaces, setCollapseSpaces] = useState(true);
  const [removeBlankLines, setRemoveBlankLines] = useState(false);
  const [trimResult, setTrimResult] = useState(true);
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () =>
      cleanWhitespace(input, {
        trimLines,
        collapseSpaces,
        removeBlankLines,
        trimResult,
      }),
    [input, trimLines, collapseSpaces, removeBlankLines, trimResult],
  );

  const copy = async () => {
    if (!result.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.wsc_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.wsc_placeholder()}
            spellCheck={false}
            className="min-h-[14rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={`${uid}-out`}>{m.wsc_output_label()}</Label>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                <span data-testid="wc-before">
                  {m.wsc_before({ n: String(result.before) })}
                </span>
                {" → "}
                <span data-testid="wc-after">
                  {m.wsc_after({ n: String(result.after) })}
                </span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copy}
                disabled={!result.text}
                className="gap-2"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-emerald-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copied ? m.wsc_copied() : m.wsc_copy()}
              </Button>
            </div>
          </div>
          <textarea
            id={`${uid}-out`}
            readOnly
            data-testid="wc-output"
            value={result.text}
            spellCheck={false}
            className="min-h-[14rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-3 rounded-xl border border-border bg-card/40 p-4">
        <Toggle
          checked={trimLines}
          onChange={setTrimLines}
          label={m.wsc_trim_lines()}
        />
        <Toggle
          checked={collapseSpaces}
          onChange={setCollapseSpaces}
          label={m.wsc_collapse()}
        />
        <Toggle
          checked={removeBlankLines}
          onChange={setRemoveBlankLines}
          label={m.wsc_remove_blank()}
        />
        <Toggle
          checked={trimResult}
          onChange={setTrimResult}
          label={m.wsc_trim_result()}
        />
      </div>
    </div>
  );
}
